import { useEffect, useState } from "react";
import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import {
	Table,
	Input,
	Select,
	Button,
	Space,
	Card,
	message,
	Modal,
	Layout,
	Form,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import AccessedCarNavigation from "../AccessedCarNavigation";
import PageHeader from "../../../common/PageHeader";

const { Content } = Layout;

const ViewEtarCars = () => {
	const [etalCarsData, setEtalCarsData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("owner");
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [addSubmitting, setAddSubmitting] = useState(false);
	const [selectedCar, setSelectedCar] = useState(null);
	const [addForm] = Form.useForm();

	useEffect(() => {
		getEtalCarsData();
		window.refreshEtarCars = getEtalCarsData;
		return () => {
			delete window.refreshEtarCars;
		};
	}, []);

	const getEtalCarsData = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get("/utils/etal-car/");
			if (response.status === 200) {
				setEtalCarsData(response.data.etal_car || []);
			}
		} catch (error) {
			message.error(
				"에타 차량 목록을 불러오는데 실패했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const filteredData = etalCarsData.filter((car) => {
		if (!searchText) return true;
		const searchValue =
			car[searchCategory]?.toString().toLowerCase() || "";
		return searchValue.includes(searchText.toLowerCase());
	});

	const handleEdit = (id) => {
		window.open(`/utils/etal-car/edit-etar-car/${id}/`, "popup");
	};

	const handleDelete = (car) => {
		setSelectedCar(car);
		setDeleteModalVisible(true);
	};

	const handleAddCar = async (values) => {
		try {
			setAddSubmitting(true);
			const response = await useAxios.post("/utils/etal-car/", {
				inst: values.inst || "",
				car_num: values.car_num,
				owner: values.owner,
			});
			if (response.status === 201) {
				message.success("차량이 성공적으로 등록되었습니다.");
				setAddModalVisible(false);
				addForm.resetFields();
				getEtalCarsData();
			}
		} catch (error) {
			message.error(
				error.response?.data?.message ||
					"차량 등록 중 오류가 발생했습니다.",
			);
		} finally {
			setAddSubmitting(false);
		}
	};

	const confirmDelete = async () => {
		try {
			const response = await useAxios.delete(
				`/utils/etal-car/${selectedCar.id}/`,
			);
			if (response.status === 200) {
				message.success("차량이 삭제되었습니다.");
				getEtalCarsData();
			}
		} catch (error) {
			message.error("차량 삭제 중 오류가 발생했습니다.");
		} finally {
			setDeleteModalVisible(false);
			setSelectedCar(null);
		}
	};

	const columns = [
		{
			title: "소속",
			dataIndex: "inst",
			key: "inst",
			sorter: (a, b) =>
				(a.inst || "").localeCompare(b.inst || ""),
		},
		{
			title: "차량번호",
			dataIndex: "car_num",
			key: "car_num",
			sorter: (a, b) => a.car_num.localeCompare(b.car_num),
		},
		{
			title: "차주",
			dataIndex: "owner",
			key: "owner",
			sorter: (a, b) => a.owner.localeCompare(b.owner),
		},
		{
			title: "관리",
			key: "action",
			render: (_, record) => (
				<Space size="middle">
					<Button
						type="primary"
						icon={<EditOutlined />}
						onClick={() =>
							handleEdit(record.id)
						}>
						수정
					</Button>
					<Button
						danger
						icon={<DeleteOutlined />}
						onClick={() =>
							handleDelete(record)
						}>
						삭제
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<PageTitle title="이탈 차량 리스트" />
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-accessed_car__container page">
							<PageHeader
								title="이탈 차량 리스트"
								actions={
									<>
										<div className="utils-accessed_car__header__toolbar">
									<Space
										wrap
										size={
											[
												8,
												8,
											]
										}>
										<Select
											defaultValue="car_num"
											style={{
												width: 120,
											}}
											onChange={
												handleCategoryChange
											}
											options={[
												{
													value: "car_num",
													label: "차량번호",
												},
												{
													value: "owner",
													label: "차주",
												},
											]}
										/>
										<Input.Search
											placeholder="검색어를 입력하세요"
											allowClear
											onSearch={
												handleSearch
											}
											className="utils-accessed_car__search"
										/>
										<Button
											type="primary"
											icon={
												<PlusOutlined />
											}
											onClick={() => {
												addForm.resetFields();
												setAddModalVisible(true);
											}}>
											차량
											추가
										</Button>
									</Space>
								</div>
									</>
								}
							/>
							<div className="utils-accessed_car__content-container">
								<Card className="surface-card utils-accessed_car__table-card">
									<Table
										columns={
											columns
										}
										dataSource={
											filteredData
										}
										rowKey="id"
										loading={
											loading
										}
										scroll={{
											x: 720,
										}}
										tableLayout="fixed"
										sticky={{
											offsetHeader: 64,
										}}
										pagination={{
											pageSize: 10,
										}}
									/>
								</Card>
							</div>
						</div>

						<Modal
							title="이탈 차량 등록"
							open={addModalVisible}
							onCancel={() => {
								setAddModalVisible(false);
								addForm.resetFields();
							}}
							footer={null}
							destroyOnClose
							width={520}>
							<Form
								form={addForm}
								layout="vertical"
								onFinish={handleAddCar}
								autoComplete="off">
								<Form.Item label="소속" name="inst">
									<Input placeholder="소속을 입력하세요" />
								</Form.Item>
								<Form.Item
									label="차량번호"
									name="car_num"
									rules={[
										{
											required: true,
											message: "차량번호를 입력해주세요",
										},
									]}>
									<Input placeholder="차량번호를 입력하세요" />
								</Form.Item>
								<Form.Item
									label="차주"
									name="owner"
									rules={[
										{
											required: true,
											message: "차주를 입력해주세요",
										},
									]}>
									<Input placeholder="차주 이름을 입력하세요" />
								</Form.Item>
								<Form.Item>
									<Space>
										<Button
											type="primary"
											htmlType="submit"
											loading={addSubmitting}>
											추가
										</Button>
										<Button
											onClick={() => {
												setAddModalVisible(false);
												addForm.resetFields();
											}}>
											취소
										</Button>
									</Space>
								</Form.Item>
							</Form>
						</Modal>

						<Modal
							title="차량 삭제"
							open={
								deleteModalVisible
							}
							onOk={confirmDelete}
							onCancel={() => {
								setDeleteModalVisible(
									false,
								);
								setSelectedCar(
									null,
								);
							}}
							okText="삭제"
							cancelText="취소"
							okButtonProps={{
								danger: true,
							}}>
							<p>
								정말로
								삭제하시겠습니까?
							</p>
							{selectedCar && (
								<p>
									차량번호:{" "}
									{
										selectedCar.car_num
									}
									<br />
									차주:{" "}
									{
										selectedCar.owner
									}
								</p>
							)}
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</>
	);
};

export default ViewEtarCars;
